import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell, PageHeader } from "../components/AppShell";
import { FoodCostBadge, CategoryBadge } from "../components/FoodCostBadge";
import { TrafficLight } from "../components/TrafficLight";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { catColor, euro, LOGO_URL } from "../lib/constants";
import { Wheat, ChefHat, Percent, Wallet, ArrowRight, Flame, Receipt } from "lucide-react";
import MagicChefButton from "../components/MagicChefButton";
import RestaurantBrandCard from "../components/RestaurantBrandCard";

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/dashboard")
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => {})
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    return (
        <AppShell>
            <div className="flex items-start justify-between gap-6 flex-wrap mb-10 animate-fade-up">
                <div>
                    <div className="eyebrow text-brand-gold mb-2">Panoramica</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05]">
                        Ciao {user?.name?.split(" ")[0] || "Chef"}.
                    </h1>
                    {user?.restaurant_name && (
                        <p className="mt-2 text-base text-brand-muted italic font-display">
                            {user.restaurant_name}
                        </p>
                    )}
                    <div className="mt-4">
                        <MagicChefButton variant="hero" />
                    </div>
                </div>
                <img src={LOGO_URL} alt="RENOVA360" className="h-14 w-14 object-contain opacity-70" />
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
                <StatCard
                    icon={<Wheat size={18} />}
                    label="Ingredienti"
                    value={loading ? "—" : data?.ingredients_count ?? 0}
                    tint="#c0ca33"
                    testid="kpi-ingredients"
                />
                <StatCard
                    icon={<ChefHat size={18} />}
                    label="Piatti"
                    value={loading ? "—" : data?.recipes_count ?? 0}
                    tint="#1976d2"
                    testid="kpi-recipes"
                />
                <StatCard
                    icon={<Percent size={18} />}
                    label="Food Cost medio"
                    value={loading ? "—" : (data?.avg_food_cost_pct ? `${data.avg_food_cost_pct.toFixed(1)}%` : "—")}
                    tint={
                        !data?.avg_food_cost_pct ? "#6b7280" :
                        data.avg_food_cost_pct < 30 ? "#2e7d32" :
                        data.avg_food_cost_pct < 35 ? "#ed6c02" : "#d32f2f"
                    }
                    testid="kpi-avg-fc"
                />
                <StatCard
                    icon={<Wallet size={18} />}
                    label="Totale acquisti"
                    value={loading ? "—" : euro(data?.total_spent ?? 0)}
                    tint="#c9a961"
                    testid="kpi-total-spent"
                />
            </div>

            {/* Brand personalization */}
            <section className="mt-10">
                <RestaurantBrandCard />
            </section>

            {/* Expensive dishes */}
            <section className="mt-10">
                <SectionTitle icon={<Flame size={16} />} title="Piatti più costosi" />
                {loading ? (
                    <Skeleton />
                ) : (data?.most_expensive_dishes || []).length === 0 ? (
                    <EmptyBlock text="Crea qualche piatto per vedere il classement dei costi." />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {data.most_expensive_dishes.map((r) => (
                            <button
                                key={r.id}
                                onClick={() => navigate(`/recipes/${r.id}`)}
                                data-testid={`dashboard-expensive-${r.id}`}
                                className="text-left group bg-paper rounded-xl border border-brand-line p-5 shadow-card hover:shadow-cardHover hover:border-brand-gold/50 transition-all duration-300"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="eyebrow text-brand-gold mb-1.5">{r.category}</div>
                                        <div className="flex items-center gap-2">
                                            <TrafficLight
                                                pct={r.food_cost_pct}
                                                hasPrice={r.selling_price > 0}
                                                size={12}
                                            />
                                            <h3 className="font-display text-2xl text-brand-ink leading-tight truncate">{r.name}</h3>
                                        </div>
                                    </div>
                                    <FoodCostBadge pct={r.food_cost_pct} />
                                </div>
                                <div className="mt-4 pt-4 border-t border-brand-line grid grid-cols-2 gap-4 text-sm">
                                    <Metric label="Costo/porz" value={euro(r.cost_per_portion)} />
                                    <Metric label="Prezzo" value={euro(r.selling_price)} align="right" />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            {/* Recent purchases */}
            <section className="mt-10 mb-12">
                <SectionTitle icon={<Receipt size={16} />} title="Acquisti recenti" cta={{ label: "Tutti", to: "/purchases", onNav: () => navigate("/purchases") }} />
                {loading ? (
                    <Skeleton />
                ) : (data?.recent_purchases || []).length === 0 ? (
                    <EmptyBlock text="Nessun acquisto registrato." />
                ) : (
                    <div className="bg-paper rounded-xl border border-brand-line shadow-card overflow-hidden">
                        {data.recent_purchases.map((p, i) => (
                            <div
                                key={p.id}
                                className={`flex items-center gap-4 px-4 sm:px-5 py-3.5 ${i > 0 ? "border-t border-brand-line" : ""}`}
                                data-testid={`recent-purchase-${p.id}`}
                            >
                                <span
                                    className="h-10 w-1.5 rounded-full shrink-0"
                                    style={{ background: catColor(p.ingredient_category) }}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-brand-ink truncate">
                                        {p.ingredient_name || "—"}
                                    </div>
                                    <div className="text-[11px] uppercase tracking-wider text-brand-muted">
                                        {p.date} · {p.quantity} {p.unit} {p.supplier ? `· ${p.supplier}` : ""}
                                    </div>
                                </div>
                                <div className="text-sm font-semibold text-brand-ink">{euro(p.total_price)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </AppShell>
    );
}

function StatCard({ icon, label, value, tint, testid }) {
    return (
        <div
            className="bg-paper rounded-xl border border-brand-line shadow-card p-5 hover:shadow-cardHover transition-shadow duration-300"
            data-testid={testid}
        >
            <div className="flex items-center gap-3">
                <div
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{ background: `${tint}1a`, color: tint }}
                >
                    {icon}
                </div>
                <div className="eyebrow text-brand-ink/60">{label}</div>
            </div>
            <div className="mt-3 font-display text-3xl lg:text-4xl text-brand-ink leading-none">{value}</div>
        </div>
    );
}

function SectionTitle({ icon, title, cta }) {
    return (
        <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
                <span className="text-brand-gold">{icon}</span>
                <h2 className="font-display text-2xl text-brand-ink">{title}</h2>
            </div>
            {cta && (
                <button
                    onClick={cta.onNav}
                    className="text-sm font-semibold text-brand-navy hover:text-brand-gold inline-flex items-center gap-1 transition link-gold"
                >
                    {cta.label} <ArrowRight size={14} />
                </button>
            )}
        </div>
    );
}

function Metric({ label, value, align = "left" }) {
    return (
        <div className={align === "right" ? "text-right" : ""}>
            <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">{label}</div>
            <div className="mt-0.5 text-sm font-semibold text-brand-ink">{value}</div>
        </div>
    );
}

function EmptyBlock({ text }) {
    return (
        <div className="bg-paper rounded-xl border border-dashed border-brand-line p-8 text-center text-sm text-brand-muted">
            {text}
        </div>
    );
}

function Skeleton() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[0, 1].map((i) => (
                <div key={i} className="bg-paper rounded-xl border border-brand-line p-5 shadow-card animate-pulse">
                    <div className="h-3 w-16 bg-brand-line rounded mb-3" />
                    <div className="h-6 w-2/3 bg-brand-line rounded" />
                </div>
            ))}
        </div>
    );
}
